import { createContext, useContext, useMemo, PropsWithChildren } from 'react';
import {
	ApolloProvider,
	useQuery,
	useMutation,
	ApolloClient,
	NormalizedCacheObject,
	QueryHookOptions,
	MutationHookOptions,
	DocumentNode,
	OperationVariables
} from '@apollo/client';
import * as Sentry from '@sentry/react';
import { SCHEMAS, clients as apolloClients } from '@/apolloClient';

// Types
type ApolloClientsContextType = {
	clients: Record<string, ApolloClient<NormalizedCacheObject>>;
	defaultClient: ApolloClient<NormalizedCacheObject>;
	useClient: (clientKey: string) => ApolloClient<NormalizedCacheObject>;
} | null;

interface MultiApolloProviderProps {
	clients?: Record<string, ApolloClient<NormalizedCacheObject>>;
	defaultClientKey?: string;
}

// Context
const ApolloClientsContext = createContext<ApolloClientsContextType>(null);

// Provider Component
export function MultiApolloProvider({
	clients = apolloClients,
	defaultClientKey = SCHEMAS.AUTHENTICATION,
	children
}: PropsWithChildren<MultiApolloProviderProps>) {
	const clientsValue = useMemo(
		() => ({
			clients,
			defaultClient: clients[defaultClientKey],
			useClient: (clientKey: string) => {
				const client = clients[clientKey];
				if (!client) {
					console.warn(`Client ${clientKey} not found, falling back to default client`);
					Sentry.captureMessage(`Apollo client ${clientKey} not found, using default`, 'warning');
					return clients[defaultClientKey];
				}
				return client;
			}
		}),
		[clients, defaultClientKey]
	);

	if (!clients[defaultClientKey]) {
		const error = new Error(`Default client ${defaultClientKey} not found in provided clients`);
		console.error(error);
		Sentry.captureException(error);
		throw error;
	}

	return (
		<ApolloClientsContext.Provider value={clientsValue}>
			<ApolloProvider client={clientsValue.defaultClient}>{children}</ApolloProvider>
		</ApolloClientsContext.Provider>
	);
}

// Hook to get a specific Apollo client
export function useApolloClient(
	clientKey?: SCHEMAS,
	skipAuth = false
): ApolloClient<NormalizedCacheObject> {
	const context = useContext(ApolloClientsContext);

	if (!context) {
		throw new Error('useApolloClient must be used within a MultiApolloProvider');
	}

	// If skipAuth is true, always use the authentication client
	if (skipAuth) {
		return context.clients[SCHEMAS.AUTHENTICATION];
	}

	return clientKey ? context.useClient(clientKey) : context.defaultClient;
}

// Hook to get all Apollo clients
export function useApolloClients(): Record<string, ApolloClient<NormalizedCacheObject>> {
	const context = useContext(ApolloClientsContext);

	if (!context) {
		throw new Error('useApolloClients must be used within a MultiApolloProvider');
	}

	return context.clients;
}

// Hook for queries with specific client
export function useClientQuery<
	TData = any,
	TVariables extends OperationVariables = OperationVariables
>(clientKey: SCHEMAS, query: DocumentNode, options: QueryHookOptions<TData, TVariables> = {}) {
	const client = useApolloClient(clientKey);

	return useQuery<TData, TVariables>(query, {
		...options,
		client
	});
}

// Hook for mutations with specific client
export function useClientMutation<
	TData = any,
	TVariables extends OperationVariables = OperationVariables
>(
	clientKey: SCHEMAS,
	mutation: DocumentNode,
	options: MutationHookOptions<TData, TVariables> = {}
) {
	const client = useApolloClient(clientKey);

	return useMutation<TData, TVariables>(mutation, {
		...options,
		client
	});
}

// Utility function to reset all Apollo caches
export async function resetAllCaches(
	clients: Record<string, ApolloClient<NormalizedCacheObject>> = apolloClients
): Promise<void> {
	try {
		await Promise.all(
			Object.values(clients).map((client) =>
				client.resetStore().catch((e) => {
					console.error('Error resetting store for client:', e);
					Sentry.captureException(e);
					return null;
				})
			)
		);
		console.log('All Apollo caches reset successfully');
	} catch (error) {
		console.error('Error resetting Apollo caches:', error);
		Sentry.captureException(error);
		throw error;
	}
}

// Type-safe hook for getting the right client type
export function getClientForSchema(schema: SCHEMAS): ApolloClient<NormalizedCacheObject> {
	const client = apolloClients[schema];
	if (!client) {
		throw new Error(`No client found for schema ${schema}`);
	}
	return client;
}

export default MultiApolloProvider;
